/**
 * Campaign mode: 2-tier structure.
 *
 * LEVELS (outer) — shown on the world map as nodes (10 themed worlds).
 *   Each LEVEL contains 10 STAGES (inner fights), one per unique opponent.
 *   Beat all 10 stages to complete a LEVEL and unlock the next.
 *
 * 100 unique opponents total — 10 per world, themed to match their realm.
 */

// ─── World opponents (10 per world = 100 total) ───────────────────────────────

export const WORLD_OPPONENTS: string[][] = [
  // World 1 — Verdant Meadow (peaceful creatures, easy)
  [
    'Dandelion',
    'Thistlewing',
    'Mossworth',
    'Cloversnap',
    'Fernbite',
    'Pepperleaf',
    'Burrowmole',
    'Thornpaw',
    'Bramblefox',
    'Ivywood',
  ],
  // World 2 — Scorched Sands (desert dwellers)
  [
    'Dustclaw',
    'Sandviper',
    'Mirage',
    'Scorchion',
    'Dune Beetle',
    'Cacthorn',
    'Heatstroke',
    'Sandstalker',
    'Sunblaze',
    'Oasis Phantom',
  ],
  // World 3 — Coral Depths (ocean / reef creatures)
  [
    'Tidesnap',
    'Coralcrusher',
    'Jellysting',
    'Kelp Phantom',
    'Sharktooth',
    'Bubble Baron',
    'Anchorjaw',
    'Reef Lurker',
    'Abyssal Eye',
    'Tidal Queen',
  ],
  // World 4 — Murky Swamp (toxic, gloomy)
  [
    'Mudgulp',
    'Bogsnare',
    'Rotwood',
    'Fungmaw',
    'Mossback',
    'Croak Lord',
    'Leech King',
    'Swamp Hag',
    'Mire Shade',
    'Bog Tyrant',
  ],
  // World 5 — Frozen Peaks (ice / mountain)
  [
    'Frostbite',
    'Iciclaw',
    'Sleetfang',
    'Blizzara',
    'Glacier Grunt',
    'Avalanche',
    'Snowstalker',
    'Permafrost',
    'Ice Wraith',
    'Cryo Baron',
  ],
  // World 6 — Inferno Crater (volcano / fire)
  [
    'Cindersnap',
    'Magmaw',
    'Lavafang',
    'Ember Imp',
    'Scorch Fiend',
    'Ashwalker',
    'Pyroclaw',
    'Molten Maw',
    'Flameguard',
    'Inferno Lord',
  ],
  // World 7 — Crystal Caverns (gems / underground)
  [
    'Shard Sprite',
    'Geode Crawler',
    'Prism Bug',
    'Crystal Fang',
    'Gem Golem',
    'Stalaclaw',
    'Quartz Beast',
    'Echo Stone',
    'Radiant Eye',
    'Crystal Tyrant',
  ],
  // World 8 — Shadow Grove (haunted forest / dark)
  [
    'Whisper',
    'Nightshade',
    'Gloomfang',
    'Banshee Root',
    'Phantom Bark',
    'Hollowmaw',
    'Dread Vine',
    'Soul Wisp',
    'Shade Walker',
    'Forest Wraith',
  ],
  // World 9 — Storm Citadel (sky / lightning)
  [
    'Gustwing',
    'Thunder Imp',
    'Bolt Hawk',
    'Cyclone',
    'Storm Sprite',
    'Nimbus',
    'Voltclaw',
    'Tempest Eye',
    'Sky Serpent',
    'Storm Lord',
  ],
  // World 10 — Chaos Fortress (final realm — mechanical / dark)
  [
    'Iron Grunt',
    'Gear Phantom',
    'Rust Fiend',
    'Chrome Fang',
    'Steel Maw',
    'Wire Wraith',
    'Core Sentinel',
    'Plasma Eye',
    'Dark Engine',
    'Overlord Chaos',
  ],
]

// ─── Levels ──────────────────────────────────────────────────────────────────

export interface LevelConfig {
  name:          string
  speed:         'slow' | 'normal' | 'fast' | 'turbo'
  /** AI difficulty for each of the 10 stages (index 0-9). */
  difficulties:  number[]
  color:         number    // theme color for the node
}

export const LEVELS: LevelConfig[] = [
  {
    name:         'Verdant Meadow',
    speed:        'slow',
    difficulties: [1, 1, 1, 1, 2, 2, 2, 2, 3, 3],
    color:        0x66bb6a,
  },
  {
    name:         'Scorched Sands',
    speed:        'slow',
    difficulties: [1, 1, 2, 2, 2, 3, 3, 3, 4, 4],
    color:        0xd4a03c,
  },
  {
    name:         'Coral Depths',
    speed:        'normal',
    difficulties: [2, 2, 3, 3, 3, 4, 4, 4, 5, 5],
    color:        0x4CC9F0,
  },
  {
    name:         'Murky Swamp',
    speed:        'normal',
    difficulties: [2, 3, 3, 4, 4, 5, 5, 5, 6, 6],
    color:        0x6d8a4e,
  },
  {
    name:         'Frozen Peaks',
    speed:        'fast',
    difficulties: [3, 3, 4, 4, 5, 5, 6, 6, 7, 7],
    color:        0x81d4fa,
  },
  {
    name:         'Inferno Crater',
    speed:        'fast',
    difficulties: [3, 4, 4, 5, 5, 6, 6, 7, 7, 8],
    color:        0xffa726,
  },
  {
    name:         'Crystal Caverns',
    speed:        'fast',
    difficulties: [4, 5, 5, 6, 6, 7, 7, 8, 8, 9],
    color:        0xab47bc,
  },
  {
    name:         'Shadow Grove',
    speed:        'turbo',
    difficulties: [5, 5, 6, 6, 7, 7, 8, 8, 9, 9],
    color:        0x5c4a6e,
  },
  {
    name:         'Storm Citadel',
    speed:        'turbo',
    difficulties: [5, 6, 6, 7, 7, 8, 8, 9, 9, 10],
    color:        0x42a5f5,
  },
  {
    name:         'Chaos Fortress',
    speed:        'turbo',
    difficulties: [6, 6, 7, 7, 8, 8, 9, 9, 10, 10],
    color:        0xe53935,
  },
]

export const STAGES_PER_LEVEL = 10

// ─── Progress ──────────────────────────────────────────────────────────────────

export interface CampaignProgress {
  /** Which level the player is on (0-based). levels.length = completed campaign. */
  currentLevel:  number
  /** Within the current level, how many stages have been cleared (0-10). */
  currentStage:  number
  /** Total accumulated score across all fights. */
  totalScore:    number
  /** Per-level, per-stage scores. stageScores[level][stage]. */
  stageScores:   number[][]
  /** Total retries (losses). */
  retries:       number
}

const STORAGE_KEY = 'beanstorm_campaign'

export function loadCampaignProgress(): CampaignProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const d = JSON.parse(raw)
      if (typeof d.currentLevel === 'number' && typeof d.currentStage === 'number') return d
    }
  } catch { /* ignore */ }
  return { currentLevel: 0, currentStage: 0, totalScore: 0, stageScores: [[]], retries: 0 }
}

export function saveCampaignProgress(p: CampaignProgress): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(p))
}

export function resetCampaignProgress(): void {
  localStorage.removeItem(STORAGE_KEY)
}

/** Apply a fight result. Returns updated progress. */
export function applyCampaignResult(
  level: number,
  stage: number,
  won: boolean,
  score: number,
): CampaignProgress {
  const p = loadCampaignProgress()

  const isCurrentFight = level === p.currentLevel && stage === p.currentStage
  const isReplay = level < p.currentLevel ||
    (level === p.currentLevel && stage < p.currentStage)

  if (isCurrentFight && won) {
    // Advance progress
    while (p.stageScores.length <= level) p.stageScores.push([])
    p.stageScores[level][stage] = score
    p.totalScore += score
    p.currentStage++

    if (p.currentStage >= STAGES_PER_LEVEL) {
      p.currentLevel++
      p.currentStage = 0
      while (p.stageScores.length <= p.currentLevel) p.stageScores.push([])
    }
  } else if (isReplay && won) {
    // Replay: update score if better, but don't change progress
    while (p.stageScores.length <= level) p.stageScores.push([])
    const prev = p.stageScores[level][stage] ?? 0
    if (score > prev) {
      p.totalScore += score - prev
      p.stageScores[level][stage] = score
    }
  } else if (isCurrentFight && !won) {
    p.retries++
  }
  // Replay losses: nothing changes

  saveCampaignProgress(p)
  return p
}
